// packages/types/src/entity-vocabularies.ts
// Canonical, dependency-light vocabularies for filterable entity fields that are
// NOT signal / stage / disposition (those live in signal-type-registry,
// project-stages, signal-types). Single source of truth shared by the Data API
// grammar (data-api.ts), the web filter bar (@parcel/filters), and the Drizzle
// pgEnums (@parcel/db imports the *_VALUES). Keep this file import-free so
// @parcel/db can consume it without pulling the @parcel/types barrel.

export interface EntityEnumDefinition {
  value: string;
  label: string;
  description: string;
}

// --- primary_use (DB pgEnum global_index.primary_use) ------------------------
export const PRIMARY_USE_VALUES = [
  'multifamily',
  'mixed_use',
  'office',
  'industrial',
  'retail',
  'hotel',
  'institutional',
  'educational',
  'r_and_d',
  'other',
] as const;

export const PRIMARY_USE_DEFINITIONS: readonly EntityEnumDefinition[] = [
  {
    value: 'multifamily',
    label: 'Residential',
    description:
      'Multifamily residential development: apartments, condominiums, or mixed-income housing.',
  },
  {
    value: 'mixed_use',
    label: 'Mixed use',
    description:
      'A project combining residential units with ground-floor retail, office, or other non-residential uses.',
  },
  {
    value: 'office',
    label: 'Office',
    description: 'Commercial office or co-working space development.',
  },
  {
    value: 'industrial',
    label: 'Industrial',
    description: 'Industrial, warehouse, manufacturing, or last-mile logistics development.',
  },
  {
    value: 'retail',
    label: 'Retail',
    description:
      'Stand-alone retail or commercial development (excludes ground-floor retail within a mixed-use project).',
  },
  {
    value: 'hotel',
    label: 'Hospitality',
    description: 'Hotel, extended-stay, or other hospitality and lodging development.',
  },
  {
    value: 'institutional',
    label: 'Institutional',
    description:
      'Civic facilities, healthcare buildings, places of worship, community centers, or other institutional uses.',
  },
  {
    value: 'educational',
    label: 'Educational',
    description:
      'Schools, universities, childcare centers, or other educational campus development.',
  },
  {
    value: 'r_and_d',
    label: 'R&D',
    description: 'Life sciences, laboratory, or research and development facility development.',
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Use type has not been classified or does not fit the standard categories.',
  },
];

// --- account_type (DB pgEnum global_index.account_type) ----------------------
export const ACCOUNT_TYPE_VALUES = [
  'developer',
  'architect',
  'gc',
  'engineer',
  'agency',
  'other',
  'law_firm',
  'consultant',
  'landscape_architect',
] as const;

export const ACCOUNT_TYPE_DEFINITIONS: readonly EntityEnumDefinition[] = [
  {
    value: 'developer',
    label: 'Developer',
    description:
      'The real-estate developer or property owner driving the project. Typically the entity that submits entitlement applications and holds site control.',
  },
  {
    value: 'architect',
    label: 'Architect',
    description:
      'An architecture firm providing design services. Parcel identifies architects as the design architect of record named in permit applications and entitlement filings.',
  },
  {
    value: 'gc',
    label: 'General contractor',
    description:
      'A general contractor or construction manager responsible for building the project. Typically appears at the permit stage or later.',
  },
  {
    value: 'engineer',
    label: 'Engineer',
    description:
      'A structural, civil, MEP, or specialty engineering firm providing technical services on projects.',
  },
  {
    value: 'agency',
    label: 'Agency',
    description:
      'A government body or public authority involved in the project (e.g. planning board, transit authority, housing agency).',
  },
  {
    value: 'other',
    label: 'Unknown',
    description:
      "This account's type has not been classified yet. Parcel will attempt to resolve it as more signal data accumulates.",
  },
  {
    value: 'law_firm',
    label: 'Law firm',
    description:
      "Legal counsel for the project or developer, typically a land-use or permitting attorney named as the applicant's representative on entitlement filings.",
  },
  {
    value: 'consultant',
    label: 'Consultant',
    description:
      'A non-engineering consultant providing services such as planning, environmental, traffic, community outreach, or project management.',
  },
  {
    value: 'landscape_architect',
    label: 'Landscape architect',
    description:
      'A landscape architecture firm providing site design and open-space planning services, named as the landscape architect of record on filings.',
  },
];

// --- project_account_role (DB pgEnum global_index.project_account_role) -------
// The role an account plays ON a project: account_type minus 'agency' (a type, not a role),
// plus 'owner' (a role, never a type).
export const PROJECT_ACCOUNT_ROLE_VALUES = [
  'developer',
  'owner',
  'architect',
  'gc',
  'engineer',
  'other',
  'law_firm',
  'consultant',
  'landscape_architect',
] as const;

export const PROJECT_ACCOUNT_ROLE_DEFINITIONS: readonly EntityEnumDefinition[] = [
  {
    value: 'developer',
    label: 'Developer',
    description:
      'The proponent, applicant, or sponsor advancing this specific project through review and construction.',
  },
  {
    value: 'owner',
    label: 'Owner',
    description:
      'The entity holding title to, or site control of, the property for this project. Often the same company as the developer; recorded separately when a filing names it.',
  },
  {
    value: 'architect',
    label: 'Architect',
    description:
      'The architecture firm providing design services on this project, as named in its filings or permits.',
  },
  {
    value: 'gc',
    label: 'General contractor',
    description:
      'The general contractor or construction manager for this project, typically named at the permit stage.',
  },
  {
    value: 'engineer',
    label: 'Engineer',
    description:
      'An engineering firm (structural, civil, MEP, or specialty) named on this specific project.',
  },
  {
    value: 'other',
    label: 'Other',
    description: 'A firm involved in this project whose specific role has not been classified.',
  },
  {
    value: 'law_firm',
    label: 'Law firm',
    description: 'The land-use or permitting attorney representing the applicant on this project.',
  },
  {
    value: 'consultant',
    label: 'Consultant',
    description:
      'A non-engineering consultant (planning, environmental, traffic, outreach, PM) named on this project.',
  },
  {
    value: 'landscape_architect',
    label: 'Landscape architect',
    description:
      'The landscape architecture firm providing site and open-space design on this project.',
  },
];

// --- contact_role (DB pgEnum global_index.contact_role) ----------------------
export const CONTACT_ROLE_VALUES = [
  'developer_contact',
  'architect_contact',
  'engineer_contact',
  'gc_contact',
  'agency_pm',
  'attorney',
  'owner_contact',
  'other',
] as const;

export const CONTACT_ROLE_DEFINITIONS: readonly EntityEnumDefinition[] = [
  {
    value: 'developer_contact',
    label: 'Developer contact',
    description: 'A person at the developer or property owner entity on this project.',
  },
  {
    value: 'architect_contact',
    label: 'Architect contact',
    description: 'A person at the architecture firm on this project.',
  },
  {
    value: 'engineer_contact',
    label: 'Engineer contact',
    description: 'A person at the engineering firm on this project.',
  },
  {
    value: 'gc_contact',
    label: 'GC contact',
    description: 'A person at the general contractor or construction manager on this project.',
  },
  {
    value: 'agency_pm',
    label: 'Agency PM',
    description:
      'A project manager at the public agency overseeing review or approval of this project.',
  },
  {
    value: 'attorney',
    label: 'Attorney',
    description: 'The land-use or permitting attorney representing the applicant on this project.',
  },
  {
    value: 'owner_contact',
    label: 'Owner contact',
    description:
      'A person at the property owner entity, in cases where the owner is distinct from the developer.',
  },
  {
    value: 'other',
    label: 'Other',
    description: 'A contact whose specific role on this project has not been classified.',
  },
];

export const ICP_BUCKET_VALUES = ['tier_1', 'tier_2', 'tier_3', 'disqualified'] as const;
export const ATLAS_BUCKET_VALUES = ['priority', 'secondary', 'watch', 'pass'] as const;
export const PROSPECT_BUCKET_VALUES = ['excellent', 'good', 'fair', 'not_a_fit'] as const;

const BUCKET_BANDS = [
  '75 or above',
  '50 to 74',
  '25 to 49',
  'below 25, or a disqualify criterion matched',
];
function bucketDefinitions(
  values: readonly string[],
  labels: readonly string[],
  score: string,
): readonly EntityEnumDefinition[] {
  return values.map((value, i) => ({
    value,
    label: labels[i] as string,
    description: `The default label for ${score} ${BUCKET_BANDS[i]}; the workspace may rename it.`,
  }));
}

export const ICP_BUCKET_DEFINITIONS = bucketDefinitions(
  ICP_BUCKET_VALUES,
  ['Tier 1', 'Tier 2', 'Tier 3', 'Disqualified'],
  'an ICP Score',
);
export const ATLAS_BUCKET_DEFINITIONS = bucketDefinitions(
  ATLAS_BUCKET_VALUES,
  ['Priority', 'Secondary', 'Watch', 'Pass'],
  'an Atlas Score',
);
export const PROSPECT_BUCKET_DEFINITIONS = bucketDefinitions(
  PROSPECT_BUCKET_VALUES,
  ['Excellent', 'Good', 'Fair', 'Not a fit'],
  'a Prospect Score',
);

// --- gc_status (derived at read time from project_accounts and attribute_tags; never stored) ---
export const GC_STATUS_VALUES = ['named', 'not_yet_selected', 'unknown'] as const;
export type GcStatus = (typeof GC_STATUS_VALUES)[number];

export const GC_STATUS_DEFINITIONS: readonly EntityEnumDefinition[] = [
  {
    value: 'named',
    label: 'Named',
    description: 'A general contractor is linked to the project.',
  },
  {
    value: 'not_yet_selected',
    label: 'Not yet selected',
    description:
      'A filing states outright that no general contractor has been chosen yet, and none is linked.',
  },
  {
    value: 'unknown',
    label: 'Unknown',
    description: 'No source says whether a general contractor has been chosen.',
  },
];

// --- stories_known (whether any source states a storey count) ---
export const STORIES_KNOWN_DEFINITIONS: readonly EntityEnumDefinition[] = [
  { value: 'known', label: 'Known', description: 'A source states the number of storeys.' },
  {
    value: 'unknown',
    label: 'Unknown',
    description: 'No source states a storey count. This does not mean a single-storey building.',
  },
];

// Enrich program: mirrors packages/core/src/enrich/types.ts for packages that cannot import core.
export const ROLE_FAMILY_VALUES = [
  'development_lead',
  'project_executive',
  'development_manager',
  'business_development',
  'principal',
  'project_architect',
  'project_manager',
  'preconstruction',
] as const;
export type RoleFamilyValue = (typeof ROLE_FAMILY_VALUES)[number];

export const ROLE_FAMILY_DEFINITIONS: readonly EntityEnumDefinition[] = [
  {
    value: 'development_lead',
    label: 'Development lead',
    description: 'Director, VP or Head of Development at a developer.',
  },
  {
    value: 'project_executive',
    label: 'Project executive',
    description: 'Runs projects at an executive level.',
  },
  {
    value: 'development_manager',
    label: 'Development manager',
    description: 'Manages development projects day to day.',
  },
  {
    value: 'business_development',
    label: 'Business development',
    description: 'Wins new work for the firm.',
  },
  { value: 'principal', label: 'Principal', description: 'A principal or partner of the firm.' },
  {
    value: 'project_architect',
    label: 'Project architect',
    description: 'Leads the design of a project at an architecture firm.',
  },
  {
    value: 'project_manager',
    label: 'Project manager',
    description: 'Manages a project for the firm.',
  },
  {
    value: 'preconstruction',
    label: 'Preconstruction',
    description: 'Estimating and preconstruction at a general contractor.',
  },
];

export const EMAIL_STATUS_VALUES = [
  'verified',
  'probable',
  'catch_all',
  'unverified',
  'bounced',
] as const;
export type EmailStatusValue = (typeof EMAIL_STATUS_VALUES)[number];

export const CONTACT_SOURCE_VALUES = ['filing', 'provider'] as const;

export const CONTACT_SOURCE_DEFINITIONS: readonly EntityEnumDefinition[] = [
  {
    value: 'filing',
    label: 'Filings and documents',
    description: 'Named in a public filing or document Parcel read.',
  },
  {
    value: 'provider',
    label: 'Parcel',
    description: "Found by Parcel's people search.",
  },
];
